import {AnOrganization} from '@mintdata-wsp/common/controllers/network/generated/responses/AnOrganization';
import {AppOAuth2CredentialsResponse} from '@mintdata-wsp/common/controllers/network/generated/responses/AppOAuth2CredentialsResponse';
import {DesignWithTimestampsResponse} from '@mintdata-wsp/common/controllers/network/generated/responses/DesignWithTimestampsResponse';
import {EmbedSnippetResponse} from '@mintdata-wsp/common/controllers/network/generated/responses/EmbedSnippetResponse';
import {OrganizationResponse} from '@mintdata-wsp/common/controllers/network/generated/responses/OrganizationResponse';
import {ProjectResponse} from '@mintdata-wsp/common/controllers/network/generated/responses/ProjectResponse';
import {PublishedApplicationResponse} from '@mintdata-wsp/common/controllers/network/generated/responses/PublishedApplicationResponse';
import {PublishedDesignResponse} from '@mintdata-wsp/common/controllers/network/generated/responses/PublishedDesignResponse';
import {UserResponse} from '@mintdata-wsp/common/controllers/network/generated/responses/UserResponse';
import {DesignRef} from '@mintdata-wsp/common/state/ui/publish/PublishState';
import {OAuth2ProviderName} from '@mintdata-wsp/common/state/ui/visualDesigner/spreadsheet/OAuth2';
import {observable} from 'mobx';
import {serializable} from 'serializr';

export class Project
{
    @observable id:number;
    @observable name:string;
    @observable description:string;
    @observable numOfDesigns:number;
    @observable designs?:Design[];

    public static fromResponse(response:ProjectResponse):Project
    {
        let project = new Project();

        project.id = response.id;
        project.name = response.name;
        project.description = response.description;
        project.numOfDesigns = response.numOfDesigns;
        project.designs = response.designs.map(designResponse => Design.fromResponse(designResponse));

        return project;
    }
}

export enum SnippetPosition
{
    HEAD_START = 'HEAD_START',
    HEAD_END = 'HEAD_END',
    BODY_START = 'BODY_START',
    BODY_END = 'BODY_END',
}

export class Snippet
{
    @observable id:number;
    @observable name:string;
    @observable enabled:boolean;
    @observable embedPosition:SnippetPosition;
    @observable embedText:string;

    public static fromResponse(response:EmbedSnippetResponse):Snippet
    {
        let snippet = new Snippet();

        snippet.id = response.id;
        snippet.name = response.name;
        snippet.enabled = response.enabled;
        snippet.embedPosition = response.embedPosition;
        snippet.embedText = response.embedText;

        return snippet;
    }

    public static empty():Snippet
    {
        let snippet = new Snippet();

        snippet.id = 0;
        snippet.name = '';
        snippet.enabled = false;
        snippet.embedPosition = SnippetPosition.BODY_END;
        snippet.embedText = '';

        return snippet;
    }
}

export class Credentials
{
    @observable id:number;
    @observable name:string;
    @observable provider:OAuth2ProviderName;
    @observable clientId:string;
    @observable clientSecret:string;
    @observable scopes:string;
    @observable customDomain:string;
    @observable apiKey:string;

    public static fromResponse(response:AppOAuth2CredentialsResponse):Credentials
    {
        let credentials = new Credentials();

        credentials.id = response.id;
        credentials.name = response.name;
        credentials.provider = response.provider as OAuth2ProviderName;
        credentials.clientId = response.clientId;
        credentials.scopes = response.scopes;
        credentials.customDomain = response.customDomain;
        credentials.apiKey = response.apiKey;

        return credentials;
    }

    public static empty():Credentials
    {
        let credentials = new Credentials();

        credentials.id = 0;
        credentials.name = '';
        credentials.provider = OAuth2ProviderName.GOOGLE;
        credentials.clientId = '';
        credentials.scopes = '';
        credentials.customDomain = '';
        credentials.apiKey = '';

        return credentials;
    }
}

export type DesignId = number;
export type ProjectId = number;

export class Design
{
    @observable id:DesignId;
    @observable projectId:ProjectId;
    @observable name:string;
    @observable description:string;
    @observable orderIndex:number;
    @observable coverUrl:string;
    version:number;
    uuid:string;
    @observable createdOn:number;
    @observable accessedOn:number;
    @observable modifiedOn:number;
    @observable isGuide:boolean;

    public static fromResponse(response:DesignWithTimestampsResponse):Design
    {
        const design = new Design();

        design.id = response.id;
        design.projectId = response.projectId;
        design.name = response.name;
        design.description = response.description;
        design.version = response.version;
        design.orderIndex = response.orderIndex;
        design.uuid = response.uuid;
        design.coverUrl = response.coverUrl;
        design.createdOn = response.createdOn;
        design.accessedOn = response.accessedOn;
        design.modifiedOn = response.modifiedOn;
        design.isGuide = response.isGuide;

        return design;
    }

    public static fromDesignRef(designRef:DesignRef):Design
    {
        const design = new Design();

        design.id = designRef.designId;
        design.projectId = designRef.projectId;
        design.name = designRef.designName;
        design.version = designRef.version;
        design.uuid = designRef.uuid;

        return design;
    }
}

export interface IDesignInfo extends Design
{
    appInfo:PublishedDesignResponse | null;
    appVersion?:PublishedApplicationResponse;
}

export type OrganizationSiteId = string;

export class Organization
{
    @observable siteId:string;
    @observable name:string;
    @observable numOfProjects:number;

    public static fromAnOrganization(response:OrganizationResponse):Organization
    {
        const organization = new Organization();

        organization.siteId = response.organization.organizationSid;
        organization.name = response.organization.organizationName;
        organization.numOfProjects = response.organization.numOfProjects;

        return organization;
    }

    public static fromResponse(response:AnOrganization):Organization
    {
        const organization = new Organization();

        organization.siteId = response.organizationSid;
        organization.name = response.organizationName;
        organization.numOfProjects = response.numOfProjects;

        return organization;
    }
}

export class UserProfile
{
    @observable fullName:string;
    @observable email:string;
}

export class OrganizationProfile
{
    @observable organizationName:string;
    @observable organizationSiteId:string;
}

export type UserId = number;

export class User
{
    @serializable fullName:string;
    @serializable username:string;
    @serializable email:string;
    @serializable uuid:string;
    @observable id:UserId;
    @observable avatarUrl?:string;
    @observable isEmailVerified:boolean;

    // @observable roles:string[];

    public static initials(user:User, isUppercase:boolean = true):string
    {
        if( !user )
            return '';

        return User.initialsFromFullName(user.fullName);
    }

    public static initialsFromFullName(fullName:string, isUppercase:boolean = true)
    {
        let initials = fullName.split(' ').map((namePart:string) =>
        {
            return namePart.charAt(0);
        }).join('');
        return isUppercase ? initials.toUpperCase() : initials;
    }

    public static fromResponse(response:UserResponse):User
    {
        let user = new User();

        user.id = response.id;
        user.uuid = response.uuid;
        user.fullName = response.fullName;
        user.username = response.username;
        user.email = response.email;
        user.avatarUrl = response.avatarUrl;
        user.isEmailVerified = response.isEmailVerified;

        return user;
    }
}

export class Template
{
    @observable designId:number;
    @observable devDesignId:number;

    @observable shortTitle:string;
    @observable description:any;
    @observable shortDescription:string;

    @observable thumbnailUrl:Array<string>;
    @observable thumbnailUrlWebP:Array<string>;

    @observable publishedAppSubdomain:string;
    @observable publishedAppDevice?:string;

    @observable categories:Array<string>;
    @observable templateUrl:string;

    public static fromResponse(response:any):Template
    {
        let template:Template = new Template();

        template.designId = response.designId;
        template.devDesignId = response.devDesignId;

        template.shortTitle = response.shortTitle;
        template.description = response.description;
        template.shortDescription = response.shortDescription;

        template.thumbnailUrl = response.thumbnailUrl;
        template.thumbnailUrlWebP = response.thumbnailUrlWebP;

        template.publishedAppSubdomain = response.publishedAppSubdomain;
        template.publishedAppDevice = response.publishedAppDevice;

        template.categories = response.categories;
        template.templateUrl = response.templateUrl;

        return template;
    }
}
